// Libs
import { useState } from "react";
import ExclamationTriangleIcon from "@heroicons/react/24/outline/ExclamationTriangleIcon";
import UserIcon from "@heroicons/react/24/outline/UserIcon";
import PhoneIcon from "@heroicons/react/24/outline/PhoneIcon";
import EnvelopeIcon from "@heroicons/react/24/outline/EnvelopeIcon";
import MapPinIcon from "@heroicons/react/24/outline/MapPinIcon";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import ChevronUpIcon from "@heroicons/react/24/outline/ChevronUpIcon";

// Local
import { ExistingCustomerWithLocations } from "../../models/customer";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";

interface Props {
  matchingCustomers: ExistingCustomerWithLocations[];
  onSelectCustomer: (customer: ExistingCustomerWithLocations) => void;
  onContinueWithNew: () => void;
  isVisible: boolean;
}

export default function ExistingCustomerMatches({
  matchingCustomers,
  onSelectCustomer,
  onContinueWithNew,
  isVisible,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isVisible || matchingCustomers.length === 0) {
    return null;
  }

  return (
    <div className="mb-6 rounded-lg border-2 border-orange-200 bg-orange-50 p-4">
      <div className="flex items-start space-x-3">
        <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-orange-600" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-orange-800">
              Possible Existing Customers Found
            </h3>
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center space-x-1 text-sm text-orange-600 hover:text-orange-800 focus:outline-none"
            >
              <span>
                {isExpanded ? "Hide" : "Show"} ({matchingCustomers.length})
              </span>
              {isExpanded ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          <p className="mt-1 text-sm text-orange-700">
            We found {matchingCustomers.length} existing customer
            {matchingCustomers.length === 1 ? "" : "s"} with similar
            information.
            {isExpanded
              ? " Please review to avoid creating duplicates."
              : " Click 'Show' to review and avoid creating duplicates."}
          </p>

          {isExpanded && (
            <div className="animate-in slide-in-from-top-2 mt-4 duration-200">
              <div className="max-h-80 space-y-3 overflow-y-auto pr-2">
                {matchingCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className="rounded-md border border-orange-200 bg-white p-3 transition-all hover:shadow-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <UserIcon className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-gray-900">
                            {customer.name}
                          </span>
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium capitalize text-gray-800">
                            {customer.type}
                          </span>
                        </div>

                        <div className="mt-2 space-y-1">
                          {customer.phone && customer.phone.length > 0 && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <PhoneIcon className="h-4 w-4" />
                              <span>{customer.phone[0]}</span>
                            </div>
                          )}
                          {customer.email && customer.email.length > 0 && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <EnvelopeIcon className="h-4 w-4" />
                              <span>{customer.email[0]}</span>
                            </div>
                          )}
                          {customer.customerLocations &&
                            customer.customerLocations.length > 0 && (
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <MapPinIcon className="h-4 w-4" />
                                <span>
                                  {customer.customerLocations[0].fullAddress ||
                                    `${customer.customerLocations[0].addressLine1}, ${customer.customerLocations[0].city}, ${customer.customerLocations[0].state}`}
                                </span>
                              </div>
                            )}
                        </div>
                      </div>

                      <BaseButtonPrimary
                        type="button"
                        onClick={() => onSelectCustomer(customer)}
                        className="ml-4 whitespace-nowrap transition-all hover:scale-105"
                      >
                        Select This Customer
                      </BaseButtonPrimary>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex justify-center space-x-3 border-t border-orange-200 pt-4">
                <BaseButtonSecondary
                  type="button"
                  onClick={onContinueWithNew}
                  className="w-full transition-all hover:scale-105 sm:w-auto"
                >
                  Continue Creating New Customer
                </BaseButtonSecondary>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
